import { normalizeBibleHistoryPath } from './bibleHistoryPaths';
import { historyService } from './history';

const HISTORY_KEY = 'bible_navigation_history';
const POSITION_KEY = 'bible_history_position';
const MAX_HISTORY_ITEMS = 100;

class NavigationHistoryService {
  getHistory() {
    try {
      const history = localStorage.getItem(HISTORY_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error reading navigation history:', error);
      return [];
    }
  }

  getPosition() {
    try {
      const position = localStorage.getItem(POSITION_KEY);
      return position ? parseInt(position, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  setPosition(position) {
    try {
      localStorage.setItem(POSITION_KEY, position.toString());
    } catch (error) {
      console.error('Error saving position:', error);
    }
  }

  addToHistory(url, title = '', isBackNavigation = false) {
    try {
      const normalized = normalizeBibleHistoryPath(url);
      const urlParts = normalized.split('/').filter(Boolean);
      // Не добавляем главную страницу в историю (любой перевод)
      if (urlParts.length <= 1 || (urlParts.length === 0 && normalized === '/')) {
        return;
      }
      
      // Не добавляем в историю, если это программный переход назад/вперёд
      if (isBackNavigation) {
        return;
      }
      
      const history = this.getHistory();
      const currentPosition = this.getPosition();
      
      // Удаляем дубликаты (если последний URL такой же, не добавляем)
      if (
        history.length > 0 &&
        history[currentPosition]?.url &&
        normalizeBibleHistoryPath(history[currentPosition].url) === normalized
      ) {
        return;
      }
      
      // Если мы не в начале истории, обрезаем "будущее"
      // (как в браузере: если пошёл назад и потом перешёл на новую страницу)
      let newHistory = history;
      if (currentPosition > 0) {
        newHistory = history.slice(currentPosition);
        this.setPosition(0);
      }
      
      // Добавляем новый элемент в начало
      const newItem = {
        url: normalized,
        title,
        timestamp: Date.now()
      };
      
      newHistory.unshift(newItem);
      
      // Ограничиваем до MAX_HISTORY_ITEMS
      if (newHistory.length > MAX_HISTORY_ITEMS) {
        newHistory.splice(MAX_HISTORY_ITEMS);
      }
      
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
    } catch (error) {
      console.error('Error saving navigation history:', error);
    }
  }
  
  // Переход назад - возвращает URL или null
  goBack() {
    const history = this.getHistory();
    const currentPosition = this.getPosition();
    
    if (currentPosition < history.length - 1) {
      const newPosition = currentPosition + 1;
      this.setPosition(newPosition);
      return history[newPosition]?.url || null;
    }
    return null;
  }

  // Переход вперёд - возвращает URL или null
  goForward() {
    const currentPosition = this.getPosition();
    
    if (currentPosition > 0) {
      const newPosition = currentPosition - 1;
      this.setPosition(newPosition);
      const history = this.getHistory();
      return history[newPosition]?.url || null;
    }
    return null;
  }

  canGoBack() {
    const history = this.getHistory();
    const currentPosition = this.getPosition();
    return currentPosition < history.length - 1;
  }

  canGoForward() {
    const currentPosition = this.getPosition();
    return currentPosition > 0;
  }
  
  // Удаляем все записи главной страницы из истории
  removeMainPageFromHistory() {
    try {
      // Единый источник правды — historyService (нормализация URL + позиция)
      const history = historyService.getHistory();
      const filtered = history.filter((item) => {
        const parts = normalizeBibleHistoryPath(item.url).split('/').filter(Boolean);
        return parts.length > 1; // Оставляем только те, где есть bookId или больше
      });
      localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered));
      historyService.setPosition(0);
    } catch (error) {
      console.error('Error removing main page from history:', error);
    }
  }

  clearHistory() {
    try {
      localStorage.removeItem(HISTORY_KEY);
      localStorage.removeItem(POSITION_KEY);
    } catch (error) {
      console.error('Error clearing navigation history:', error);
    }
  }

  getPreviousUrl(currentUrl) {
    try {
      const history = this.getHistory();
      if (history.length === 0) {
        return null;
      }
      
      // Находим текущий URL в истории
      const currentIndex = history.findIndex(item => item.url === currentUrl);
      
      // Если текущий URL не найден или это первый элемент, возвращаем null
      if (currentIndex === -1 || currentIndex === history.length - 1) {
        return null;
      }
      
      // Возвращаем следующий элемент (более старый)
      return history[currentIndex + 1].url;
    } catch (error) {
      console.error('Error getting previous URL:', error);
      return null;
    }
  }

  removeFromHistory(url) {
    try {
      const history = this.getHistory();
      const filtered = history.filter(item => item.url !== url);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error removing from history:', error);
    }
  }

  // Форматируем URL для отображения
  async formatUrl(url) {
    // Убираем начальный слэш и разбиваем на части
    const parts = url.replace(/^\//, '').split('/');
    
    if (parts.length === 0 || parts.length === 1) {
      return 'Главная';
    }
    
    // Первая часть - это перевод (translation)
    const translation = parts[0];
    const bookId = parseInt(parts[1]);
    
    if (!isNaN(bookId) && parts.length >= 2) {
      try {
        // Загружаем список книг для получения названия через API
        const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
        const response = await fetch(`${API_BASE}/bible/${translation}/books`);
        if (response.ok) {
          const data = await response.json();
          const book = data.books?.find(b => b.id === bookId);
          if (book) {
            if (parts.length === 2) {
              return book.name;
            } else if (parts.length === 3) {
              return `${book.name} ${parts[2]}`;
            } else if (parts.length === 4) {
              return `${book.name} ${parts[2]}:${parts[3]}`;
            }
          }
        }
      } catch (error) {
        console.error('Error loading books for formatUrl:', error);
      }
      
      // Fallback если не удалось загрузить
      if (parts.length === 2) {
        return `Книга ${parts[1]}`;
      } else if (parts.length === 3) {
        return `Книга ${parts[1]}, Глава ${parts[2]}`;
      } else if (parts.length === 4) {
        return `Книга ${parts[1]}, Глава ${parts[2]}, Стих ${parts[3]}`;
      }
    }
    
    return url;
  }
}

export const navigationHistoryService = new NavigationHistoryService();

